import Cookies from "js-cookie";
import { PUBLIC_API_URL } from "./api";

const TOKEN_COOKIE = "construxia_token";
const REFRESH_COOKIE = "construxia_refresh";
const USER_COOKIE = "construxia_user";
const NEXT_LOCALE_COOKIE = "NEXT_LOCALE";

const SUPPORTED_LOCALES = new Set(["en", "de", "pl"]);

export type UserRole = "OFFICER" | "EXPERT" | "ADMIN";

export interface AuthUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  signatory_title?: string;
  company_id: string | null;
  company_name?: string;
  signature_url?: string | null;
  is_construxia_admin: boolean;
  role?: UserRole;
  can_view_technical_data?: boolean;
  can_access_audit_lab?: boolean;
  /** Opt-in feature keys enabled for the user's company (new functionality only). */
  features?: string[];
}

export function getUserRole(user: AuthUser | null | undefined): UserRole {
  if (!user) return "OFFICER";
  if (user.is_construxia_admin) return "ADMIN";
  return (user.role as UserRole) || "OFFICER";
}

export function canViewTechnicalData(user: AuthUser | null | undefined): boolean {
  if (!user) return false;
  if (typeof user.can_view_technical_data === "boolean") {
    return user.can_view_technical_data;
  }
  const role = getUserRole(user);
  return role === "EXPERT" || role === "ADMIN";
}

export function canAccessAuditLab(user: AuthUser | null | undefined): boolean {
  if (!user) return false;
  if (typeof user.can_access_audit_lab === "boolean") {
    return user.can_access_audit_lab;
  }
  const role = getUserRole(user);
  return role === "EXPERT" || role === "ADMIN";
}

/** Company Administrator (or Construxia super-admin): portfolio structure, vault UI, regulations admin. */
export function isCompanyAdmin(user: AuthUser | null | undefined): boolean {
  return getUserRole(user) === "ADMIN";
}

/** ADMIN or EXPERT: allowed to create folders and products in the portfolio. */
export function canManagePortfolio(user: AuthUser | null | undefined): boolean {
  const role = getUserRole(user);
  return role === "ADMIN" || role === "EXPERT";
}

/** True when the user's company has an opt-in feature key enabled. */
export function hasFeature(
  user: AuthUser | null | undefined,
  featureKey: string,
): boolean {
  if (!user || !featureKey) return false;
  return (user.features ?? []).includes(featureKey);
}

interface LoginResponse {
  access: string;
  refresh: string;
  user: AuthUser;
}

function resolveLocale(): string {
  const cookieLocale = Cookies.get(NEXT_LOCALE_COOKIE);
  if (cookieLocale && SUPPORTED_LOCALES.has(cookieLocale)) {
    return cookieLocale;
  }
  if (typeof window !== "undefined") {
    const segment = window.location.pathname.split("/")[1] || "";
    if (SUPPORTED_LOCALES.has(segment)) {
      return segment;
    }
  }
  return "en";
}

// ---------------------------------------------------------------------------
// Token / User persistence (cookies)
// ---------------------------------------------------------------------------

export function getToken(): string | undefined {
  return Cookies.get(TOKEN_COOKIE);
}

export function getRefreshToken(): string | undefined {
  return Cookies.get(REFRESH_COOKIE);
}

export function getUser(): AuthUser | null {
  const raw = Cookies.get(USER_COOKIE);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function persistAuth(access: string, refresh: string, user: AuthUser) {
  const secure = window.location.protocol === "https:";
  const opts: Cookies.CookieAttributes = { path: "/", sameSite: "lax", secure };

  Cookies.set(TOKEN_COOKIE, access, { ...opts, expires: 1 / 24 }); // ~60 min
  Cookies.set(REFRESH_COOKIE, refresh, { ...opts, expires: 7 });
  Cookies.set(USER_COOKIE, JSON.stringify(user), { ...opts, expires: 7 });
}

function clearCookies() {
  Cookies.remove(TOKEN_COOKIE, { path: "/" });
  Cookies.remove(REFRESH_COOKIE, { path: "/" });
  Cookies.remove(USER_COOKIE, { path: "/" });
}

// ---------------------------------------------------------------------------
// API actions
// ---------------------------------------------------------------------------

function apiUnreachableMessage(): string {
  return (
    `Cannot reach the API at ${PUBLIC_API_URL}. ` +
    "Start the Django backend (port 8000 by default), set NEXT_PUBLIC_API_URL in " +
    ".env to the same host you use in the browser (not localhost if you open the " +
    "app via an IP or HTTPS), and ensure CORS allows this frontend origin."
  );
}

export async function login(
  email: string,
  password: string,
): Promise<AuthUser> {
  let res: Response;
  try {
    res = await fetch(`${PUBLIC_API_URL}/api/auth/login/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept-Language": resolveLocale(),
      },
      body: JSON.stringify({ email, password }),
    });
  } catch (e) {
    const isNetwork =
      e instanceof TypeError ||
      (e instanceof Error && /failed to fetch|networkerror|load failed/i.test(e.message));
    throw new Error(isNetwork ? apiUnreachableMessage() : String(e));
  }

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.detail ?? `Login failed (${res.status})`);
  }

  const data: LoginResponse = await res.json();
  persistAuth(data.access, data.refresh, data.user);
  return data.user;
}

export function logout() {
  const locale = resolveLocale();
  clearCookies();
  window.location.href = `/${locale}/login`;
}

export function getMe(): AuthUser | null {
  return getUser();
}

// ---------------------------------------------------------------------------
// Authenticated fetch (reads token from cookie)
// ---------------------------------------------------------------------------

export async function authFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const token = getToken();

  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> | undefined),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  headers["Accept-Language"] = resolveLocale();

  if (init.body && typeof init.body === "string" && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  const url = `${PUBLIC_API_URL}${path}`;
  const res = await fetch(url, { ...init, headers });

  if (res.status === 401) {
    const locale = resolveLocale();
    clearCookies();
    if (typeof window !== "undefined") {
      window.location.href = `/${locale}/login`;
    }
  }

  return res;
}
